import React from 'react';
import { useTranslation } from 'react-i18next';

import { EntryCard, EntryCashOutControls, EntryStatus } from '@/components/entry-card';
import type { EntryCardPayoutProps } from '@/components/entry-card';
import { Bet } from '@/feature/bets-sbk/hooks/types';
import { useFooter } from '@/feature/bets-sbk/hooks/use-bet-card';
import { useIsUnmatchedPrediction } from '@/feature/bets-sbk/hooks/use-is-unmatched-prediction';
import { usePotentialPayout } from '@/feature/bets-sbk/hooks/use-potential-payout';
import { useSbkEntryCashOut } from '@/feature/bets-sbk/hooks/use-sbk-entry-cash-out';
import { classifyBetVariant } from '@/feature/bets-sbk/utils/classify-bet-variant';
import { useFormatCurrency } from '@/hooks/use-format-currency';

import { SbkEntryCardBottom } from '../SbkEntryCardBottom';
import { useSbkCurrencyIcon, useSbkEntryStake } from './sbk-entry-card-currency';
import { useSbkEntryHeader } from './use-sbk-entry-header';
import { useSbkEntryPromos } from './use-sbk-entry-promos';
import { useSbkEntryStatus } from './use-sbk-entry-status';

/** Payout block for an open bet — entry stake and potential payout, with bonus-entry styling. */
const useOpenPayout = (bet: Bet): EntryCardPayoutProps => {
    const { t } = useTranslation('bets');
    const { formatCurrency } = useFormatCurrency();
    const payout = usePotentialPayout(bet);

    return {
        ...useSbkEntryStake(bet),
        payoutLabel: t('payout'),
        payoutAmount: formatCurrency(payout),
        payoutIcon: useSbkCurrencyIcon(),
        payoutColor: 'neutral300',
    };
};

type Props = {
    bet: Bet;
    onPress: () => void;
    onSharePress: () => void;
};

/** Open (pending) sportsbook bet rendered on the shared entry card — Single, Parlay, SGP and SGP+. */
export const SbkOpenEntryCard = ({ bet, onPress, onSharePress }: Props) => {
    const variant = classifyBetVariant(bet);
    const baseHeader = useSbkEntryHeader(bet, variant);
    const basePayout = useOpenPayout(bet);
    const promos = useSbkEntryPromos(bet, false);
    const placedLine = useFooter(bet);
    const cashOut = useSbkEntryCashOut(bet);

    // The cash-out phase drives the header badge, payout column, and the button slot; the footer is unchanged.
    const cashOutStatus = useSbkEntryStatus(cashOut.status);
    const status = useIsUnmatchedPrediction(bet) ? EntryStatus.Unconfirmed : cashOutStatus;
    const header = { ...baseHeader, status };
    const payout = { ...basePayout, ...cashOut.payout };

    return (
        <EntryCard
            variant="sbk"
            header={header}
            payout={payout}
            promos={promos}
            bottom={<SbkEntryCardBottom dateLine={placedLine} entryId={bet.id} onSharePress={onSharePress} />}
            cashOut={cashOut.controls ? <EntryCashOutControls {...cashOut.controls} /> : undefined}
            onPress={onPress}
        />
    );
};
